/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return */
import { action, observable } from 'mobx';
import type { Client, Components } from 'types/next-api';
import { StoreState } from 'enums/common';

type DeliveryCode = Components.Schemas.DeliveryCode;

export default class DeliveryCodesStore {
  client: Client;

  @observable deliveryCodes: DeliveryCode[] = [];
  @observable status: StoreState = StoreState.ready;
  @observable deliveryCodeInUse: Array<{ inUse?: boolean; deliveryCodeId?: number }> = [];
  private currentlyUsedChainIds: string[] = [];

  @action
  async getDeliveryCodesByChain(chainIds: string[]) {
    try {
      const result = await this.client.searchDeliveryCodes(null, {
        chainId: [chainIds[0], ...chainIds.slice(1)],
      });
      this.deliveryCodes = result.data;
      this.currentlyUsedChainIds = [...chainIds];
      this.status = StoreState.ready;
    } catch {
      this.status = StoreState.error;
    }
  }

  @action
  async getAllDeliveryCodes() {
    try {
      // Get all delivery codes by searching with empty criteria
      const result = await this.client.searchDeliveryCodes(null, {});
      this.deliveryCodes = result.data;
      this.status = StoreState.ready;
    } catch {
      this.status = StoreState.error;
    }
  }

  @action
  async isDeliveryCodeInUse(deliveryCodeId: string) {
    try {
      const result = await this.client.isDeliveryCodeInUse(deliveryCodeId);
      this.deliveryCodeInUse = [{ inUse: result.data.inUse, deliveryCodeId: Number(deliveryCodeId) }];
      this.status = StoreState.ready;
      return result.data.inUse;
    } catch {
      this.status = StoreState.error;
      return false;
    }
  }

  @action
  async createDeliveryCode(deliveryCode: DeliveryCode) {
    try {
      const result = await this.client.createDeliveryCode(null, deliveryCode);
      // Refresh the list after creation
      if (this.currentlyUsedChainIds.length > 0) {
        await this.getDeliveryCodesByChain(this.currentlyUsedChainIds);
      } else {
        await this.getAllDeliveryCodes();
      }
      this.status = StoreState.ready;
      return result.data;
    } catch {
      this.status = StoreState.error;
    }
  }

  @action
  async updateDeliveryCode(deliveryCode: DeliveryCode) {
    try {
      const result = await this.client.updateDeliveryCode(deliveryCode.id, deliveryCode);
      // Refresh the list after update
      if (this.currentlyUsedChainIds.length > 0) {
        await this.getDeliveryCodesByChain(this.currentlyUsedChainIds);
      } else {
        await this.getAllDeliveryCodes();
      }
      this.status = StoreState.ready;
      return result.data;
    } catch {
      this.status = StoreState.error;
    }
  }

  @action
  async deleteDeliveryCode(deliveryCodeId: string) {
    try {
      await this.client.deleteDeliveryCode(deliveryCodeId);
      // Refresh the list after deletion
      if (this.currentlyUsedChainIds.length > 0) {
        await this.getDeliveryCodesByChain(this.currentlyUsedChainIds);
      } else {
        await this.getAllDeliveryCodes();
      }
      this.status = StoreState.ready;
    } catch {
      this.status = StoreState.error;
    }
  }

  /**
   * Check if any mandatory delivery codes exist for the given chain IDs
   */
  hasMandatoryDeliveryCode(chainIds: string[]): boolean {
    return this.deliveryCodes.some(
      (code) => code.mandatory && code.chainIds.some((chainId) => chainIds.includes(chainId)),
    );
  }
}
